// ─── University Profile System ────────────────────────────────────────────────
// Each profile defines structural rules, templates, and formatting standards
// for a specific university's thesis format.
//
// Profile keys are used in thesis_front_matter.university_profile.

// ── Template interpolation ────────────────────────────────────────────────────

export function interpolate(template, vars) {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] || `[${key}]`)
}

// ── Profile definitions ───────────────────────────────────────────────────────

export const PROFILES = {

  kennedy: {
    id:          'kennedy',
    name:        'Kennedy University',
    citation:    'APA 7th Edition',
    description: 'Kennedy University DBA/PhD format',

    // Page layout (used in export CSS)
    layout: {
      fontFamily:    '"Times New Roman", Georgia, serif',
      fontSize:      '12pt',
      lineHeight:    '2',          // double-spaced
      marginTop:     '1in',
      marginBottom:  '1in',
      marginLeft:    '1.5in',      // wider for binding
      marginRight:   '1in',
      chapterBreak:  true,         // each chapter starts on a new page
    },

    // Heading styles
    headings: {
      h1: { fontWeight: 'bold', fontSize: '14pt', textAlign: 'center', textTransform: 'uppercase', pageBreakBefore: true },
      h2: { fontWeight: 'bold', fontSize: '12pt', textAlign: 'left' },
      h3: { fontWeight: 'bold', fontSize: '12pt', textAlign: 'left', fontStyle: 'italic' },
      h4: { fontWeight: 'bold', fontSize: '12pt', textAlign: 'left' },
    },

    // Figure / table captions
    captions: {
      figurePrefix: 'Figure',   // "Figure 4.1: ..."
      tablePrefix:  'Table',    // "Table 3.2: ..."
      position:     'below',    // caption appears below the visual
      style:        'italic',
    },

    // Front matter section ordering
    frontMatterOrder: [
      'title',
      'certificate_supervisor',
      'certificate_examiner',
      'declaration',
      'dedication',
      'acknowledgements',
      'abstract',
      'toc',
      'list_of_figures',
      'list_of_tables',
      'abbreviations',
    ],

    // Templates — {{variable}} placeholders interpolated at render time
    templates: {

      title: `
<div class="fm-page title-page" style="text-align:center;padding:2in 0 1in">
  <div style="margin-bottom:1.5in">
    <p class="thesis-title" style="font-size:16pt;font-weight:bold;text-transform:uppercase;line-height:1.4">{{thesis_title}}</p>
  </div>
  <p style="margin-bottom:0.5in">A Thesis Submitted in Partial Fulfilment of the Requirements<br>for the Degree of</p>
  <p style="font-weight:bold;margin-bottom:0.5in">{{degree}} in {{specialization}}</p>
  <p style="margin-bottom:1in">Submitted by:<br>
  <strong>{{student_name}}</strong><br>
  Registration No.: {{reg_number}}</p>
  <p>Under the Supervision of:<br>
  <strong>{{supervisor_name}}</strong><br>
  {{supervisor_designation}}<br>
  {{supervisor_institution}}</p>
  <div style="position:absolute;bottom:1.5in;width:100%;text-align:center">
    <p style="font-weight:bold">{{university_name}}</p>
    <p>{{faculty_name}}</p>
    <p>{{submission_month}} {{submission_year}}</p>
  </div>
</div>`,

      certificate_supervisor: `
<div class="fm-page">
  <h1 style="text-align:center;font-size:14pt;font-weight:bold;text-transform:uppercase;margin-bottom:2rem">Certificate</h1>
  <p>This is to certify that the thesis entitled</p>
  <p style="font-weight:bold;text-align:center;margin:1rem 0">"{{thesis_title}}"</p>
  <p>submitted by <strong>{{student_name}}</strong> (Registration No. {{reg_number}}) in partial fulfilment of the requirements for the award of the Degree of <strong>{{degree}} in {{specialization}}</strong> from <strong>{{university_name}}</strong> is a record of genuine and original research work carried out by the candidate under my supervision. To the best of my knowledge and belief, the thesis contains no material previously published or written by another person, except where due reference is made in the text.</p>
  <p style="margin-top:3rem">I recommend that the thesis may be accepted for evaluation.</p>
  <div style="margin-top:3rem">
    <p>________________________</p>
    <p><strong>{{supervisor_name}}</strong></p>
    <p>{{supervisor_designation}}</p>
    <p>{{supervisor_institution}}</p>
    <p style="margin-top:1rem">Date: ____________________</p>
  </div>
</div>`,

      certificate_examiner: `
<div class="fm-page">
  <h1 style="text-align:center;font-size:14pt;font-weight:bold;text-transform:uppercase;margin-bottom:2rem">Certificate of the Examiner</h1>
  <p>This is to certify that the thesis entitled</p>
  <p style="font-weight:bold;text-align:center;margin:1rem 0">"{{thesis_title}}"</p>
  <p>submitted by <strong>{{student_name}}</strong> has been examined by the undersigned. The thesis has been accepted as fulfilling the requirements for the award of the Degree of <strong>{{degree}} in {{specialization}}</strong>.</p>
  <div style="margin-top:3rem">
    <p>________________________</p>
    <p><strong>{{examiner_name}}</strong></p>
    <p>{{examiner_designation}}</p>
    <p style="margin-top:1rem">Date: ____________________</p>
  </div>
</div>`,

      declaration: `
<div class="fm-page">
  <h1 style="text-align:center;font-size:14pt;font-weight:bold;text-transform:uppercase;margin-bottom:2rem">Declaration</h1>
  <p>I, <strong>{{student_name}}</strong>, hereby declare that the thesis entitled</p>
  <p style="font-weight:bold;text-align:center;margin:1rem 0">"{{thesis_title}}"</p>
  <p>submitted for the award of the Degree of <strong>{{degree}} in {{specialization}}</strong> is my own original work. This thesis has not been submitted for the award of any other degree or diploma in any university. The assistance and help received during the course of investigation have been duly acknowledged.</p>
  <div style="margin-top:3rem">
    <p style="float:left">Place: {{declaration_city}}</p>
    <p style="float:right">________________________</p>
    <div style="clear:both"></div>
    <p style="float:right"><strong>{{student_name}}</strong></p>
    <div style="clear:both"></div>
  </div>
</div>`,

      dedication: `
<div class="fm-page" style="display:flex;align-items:center;justify-content:center;min-height:6in;text-align:center">
  <div style="font-style:italic">{{dedication_text}}</div>
</div>`,

      acknowledgements: `
<div class="fm-page">
  <h1 style="text-align:center;font-size:14pt;font-weight:bold;text-transform:uppercase;margin-bottom:2rem">Acknowledgements</h1>
  {{acknowledgement_text}}
</div>`,

      abstract: `
<div class="fm-page">
  <h1 style="text-align:center;font-size:14pt;font-weight:bold;text-transform:uppercase;margin-bottom:2rem">Abstract</h1>
  {{abstract_text}}
  {{keywords_block}}
</div>`,
    },
  },

  // Additional profiles can be added here
  // apa_generic: { ... },
  // oxford: { ... },
}

export function getProfile(id) {
  return PROFILES[id] || PROFILES.kennedy
}

export const PROFILE_OPTIONS = Object.values(PROFILES).map(p => ({
  value: p.id,
  label: p.name,
  description: p.description,
}))
